import { useEffect, useRef, useState } from 'react';
import { getParsedComboboxData, Select } from '@mantine/core';

import styles from './autofill-select.module.css';

import type { SelectProps } from '@mantine/core';

type AutofillSelectProps = Omit<SelectProps, 'dropdownOpened' | 'onDropdownOpen' | 'onDropdownClose' | 'onSearchChange'> & {
  /** HTML autocomplete token for the hidden input (e.g. "section-shipto country-name"). */
  autoComplete: string;
  /** When true, typing an exact label match auto-selects it and closes the dropdown. Defaults to true. */
  useAutoSelect?: boolean;
};

type FlatItem = { value: string; label: string };

const flattenData = (data: SelectProps['data']): FlatItem[] => {
  const parsed = getParsedComboboxData(data);
  return parsed.flatMap((item) =>
    'group' in item
      ? item.items.map((i) => ({ value: String(i.value), label: i.label }))
      : [{ value: String(item.value), label: item.label }],
  );
};

const findMatch = (input: string, items: readonly FlatItem[]): string | null => {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;

  for (const opt of items) {
    if (opt.value.toLowerCase() === normalized) return opt.value;
  }
  for (const opt of items) {
    if (opt.label.toLowerCase() === normalized) return opt.value;
  }
  for (const opt of items) {
    if (opt.label.toLowerCase().includes(normalized)) return opt.value;
  }
  return null;
};

const findExactLabelMatch = (input: string, items: readonly FlatItem[]): string | null => {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  for (const opt of items) {
    if (opt.label.toLowerCase() === normalized) return opt.value;
  }
  return null;
};

/**
 * Wraps a Mantine Select with a hidden native input for 1Password/browser
 * autofill compatibility. The hidden input carries the autocomplete
 * attribute; when autofill sets its value, a fuzzy matcher translates it
 * into the correct Select option.
 */
export const AutofillSelect = ({ autoComplete, useAutoSelect = true, ...selectProps }: AutofillSelectProps) => {
  const { data, onChange } = selectProps;
  const hiddenRef = useRef<HTMLInputElement>(null);
  const lastAutofillValue = useRef('');
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const suppressDropdown = useRef(false);

  const handleAutofill = () => {
    const input = hiddenRef.current;
    if (!input || !input.value) return;
    lastAutofillValue.current = input.value;
    const items = flattenData(data);
    const match = findMatch(input.value, items);
    if (match && onChange) {
      onChange(match, { value: match, label: match });
    }
  };

  useEffect(() => {
    const pending = lastAutofillValue.current;
    if (!pending) return;
    const items = flattenData(data);
    const match = findMatch(pending, items);
    if (match && onChange) {
      onChange(match, { value: match, label: match });
      lastAutofillValue.current = '';
    }
  }, [data, onChange]);

  const handleSearchChange = (query: string) => {
    if (!useAutoSelect) return;
    const items = flattenData(data);
    const exactMatch = findExactLabelMatch(query, items);
    if (exactMatch) {
      suppressDropdown.current = true;
      setDropdownOpened(false);
      if (onChange) {
        onChange(exactMatch, { value: exactMatch, label: query });
      }
    }
  };

  const handleDropdownOpen = () => {
    if (suppressDropdown.current) {
      suppressDropdown.current = false;
      return;
    }
    setDropdownOpened(true);
  };

  const handleDropdownClose = () => {
    setDropdownOpened(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={hiddenRef}
        aria-hidden='true'
        autoComplete={autoComplete}
        className={styles.hiddenInput}
        onInput={handleAutofill}
        tabIndex={-1}
      />
      <Select
        allowDeselect={false}
        searchable
        {...selectProps}
        dropdownOpened={dropdownOpened}
        onDropdownClose={handleDropdownClose}
        onDropdownOpen={handleDropdownOpen}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
};
